import React from 'react';
import type { QnAStateType } from '@uber/react-qna-lib';
import { Button, KIND } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import type { Value } from 'baseui/select';
import { Select, TYPE } from 'baseui/select';
import { HeadingLarge } from 'baseui/typography';
import type { QuestionnaireT } from '../../../../common/types/types';
import { PROJECT_MAJOR_STATES } from '../../../consts/project';
import { useCreateProject } from '../../../hooks/use-create-project';
import type { AnnotationLabelWithMetadataT } from '../../../types/annotationLabelWithMetadata';
import type { QuestionnaireInputT } from '../../../types/questionnaireInput';
import type { UserGroup } from '../../../types/userGroup';
import ProjectAnnotationForm from './project-annotation-form';
import ProjectQuestionnaire from './project-questionnaire-form';
type ProjectionCreationFormProps = {
  groups: UserGroup[];
  closeForm: () => void;
};
const ProjectionCreationForm = (props: ProjectionCreationFormProps) => {
  const {
    closeForm,
    groups
  } = props;
  const [projectName, setProjectName] = React.useState<string>('');
  const [majorStates, setMajorStates] = React.useState<Value>([]);
  const [taskQuestionnaires, seTaskQuestionnaires] = React.useState<QuestionnaireT[]>([]);
  const [annotationLabels, setAnnotationLabels] = React.useState<AnnotationLabelWithMetadataT[]>([]);
  const [questionnaires, setQuestionnaires] = React.useState<QuestionnaireInputT[]>([]);
  const [group, setGroup] = React.useState<Value>([]);
  const [majorStateAfterMetaReviewReject, setMajorStateAfterMetaReviewReject] = React.useState<Value>([]);
  const {
    isPending,
    createProject
  } = useCreateProject(projectName, majorStates.map(state => state.id!.toString()), majorStateAfterMetaReviewReject[0]?.id?.toString().trim() ?? '', {
    annotationLabel: annotationLabels,
    questionnaires: questionnaires.reduce((acc, questionnaire) => {
      acc[questionnaire.questionnaireId] = {
        questionnaire: JSON.parse(questionnaire.data || '{}'),
        name: questionnaire.name
      };
      return acc;
    }, ({} as Record<string, {
      questionnaire: QnAStateType;
      name: string;
    }>))
  },
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  group && group.length > 0 ? +group[0].id! : null, taskQuestionnaires);
  const handleSubmit = () => {
    if (majorStates.length === 0) {
      console.error('At least one major state is required');
      return;
    }
    if (!group || group.length === 0) {
      console.error('User group is required');
      return;
    }
    if (!majorStateAfterMetaReviewReject || majorStateAfterMetaReviewReject.length === 0 || !majorStateAfterMetaReviewReject[0]?.id) {
      console.error('Reject state is required');
      return;
    }
    createProject({
      name: projectName,
      majorStates: majorStates.map(state => state.id!.toString()),
      majorStateAfterMetaReviewReject: majorStateAfterMetaReviewReject[0].id.toString(),
      configuration: {
        annotationLabel: annotationLabels,
        questionnaires: questionnaires.reduce((acc, questionnaire) => {
          acc[questionnaire.questionnaireId] = {
            questionnaire: JSON.parse(questionnaire.data),
            name: questionnaire.name
          };
          return acc;
        }, ({} as Record<string, {
          questionnaire: QnAStateType;
          name: string;
        }>))
      },
      taskLevelQuestionnaireList: taskQuestionnaires || null,
      // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
      userGroupId: +group[0].id!
    }, {
      onSuccess: () => {
        closeForm();
      },
      onError: error => {
        console.error('Error creating/updating project:', error);
      }
    });
  };
  const isFormInvalid = projectName === '' || majorStates.length === 0 || !group || group.length === 0 || annotationLabels.length === 0 || !majorStateAfterMetaReviewReject || majorStateAfterMetaReviewReject.length === 0;
  return <div style={{
    width: '100%'
  }}>
      <HeadingLarge>Project Creation</HeadingLarge>
      <FormControl label="Project Name" error={!projectName ? 'Project name is required' : ''}>
        <Input value={projectName} onChange={e => setProjectName(e.currentTarget.value)} placeholder="Enter project name" />
      </FormControl>
      <FormControl label="Major States" error={majorStates.length === 0 ? 'At least one major state is required' : ''}>
        <Select options={PROJECT_MAJOR_STATES.map(state => ({
        id: state
      }))} labelKey="id" valueKey="id" placeholder="Choose major states" maxDropdownHeight="300px" type={TYPE.search} multi onChange={({
        value
      }: {
        value: Value;
      }) => {
        setMajorStates(value);
      }} value={majorStates} />
      </FormControl>
      <FormControl label="Reject State from MetaReview" error={majorStateAfterMetaReviewReject.length === 0 ? 'Reject state is required' : ''}>
        <Select options={majorStates.filter(state => state.id !== 'MetaReview')} labelKey="id" valueKey="id" placeholder="Choose reject state" maxDropdownHeight="300px" type={TYPE.search} onChange={({
        value
      }: {
        value: Value;
      }) => {
        setMajorStateAfterMetaReviewReject(value);
      }} value={majorStateAfterMetaReviewReject} />
      </FormControl>
      <FormControl label="User Group" error={!group || group.length === 0 ? 'User group is required' : ''}>
        <Select options={groups} labelKey="name" valueKey="id" placeholder="Choose user groups" maxDropdownHeight="300px" type={TYPE.search} onChange={({
        value
      }: {
        value: Value;
      }) => {
        setGroup(value);
      }} value={group} />
      </FormControl>
      <ProjectQuestionnaire questionnaires={questionnaires} setQuestionnaires={setQuestionnaires} />
      <ProjectAnnotationForm questionnaires={questionnaires} annotationLabels={annotationLabels} setAnnotationLabels={setAnnotationLabels} />
      <FormControl label="Task Questionnaire">
        <Select options={questionnaires} multi labelKey="name" valueKey="questionnaireId" placeholder="Choose questionnaire for task" maxDropdownHeight="300px" type={TYPE.search} onChange={({
        value
      }: {
        value: Value;
      }) => {
        const selectedQuestionnaires = value.map(item => ({
          questionnaireId: item.questionnaireId,
          name: item.name,
          workspaceId: item.workspaceId
        }));
        seTaskQuestionnaires(selectedQuestionnaires);
      }} value={taskQuestionnaires} />
      </FormControl>
      <ButtonGroup kind={KIND.primary}>
        <Button onClick={() => closeForm()}>Close</Button>
        <Button disabled={isFormInvalid} onClick={handleSubmit}>
          Submit
        </Button>
      </ButtonGroup>
    </div>;
};
export default ProjectionCreationForm;